import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Bot, Plus, Unlink, Settings, Sparkles, Users, Zap } from 'lucide-react';
import type { CustomAction } from '@/types/action';
interface ActionConnectionsTabProps {
  action: CustomAction;
}
export const ActionConnectionsTab = ({
  action
}: ActionConnectionsTabProps) => {
  const connectedChatbots = action.connectedChatbots?.filter(bot => bot.type !== 'copilot') || [];
  const connectedCopilots = action.connectedChatbots?.filter(bot => bot.type === 'copilot') || [];

  // Legacy support for actions that might have chatbots in the old format
  const legacyChatbots = action.chatbots || [];
  const allChatbots = [...connectedChatbots, ...legacyChatbots];
  return <div className="space-y-8">
      {/* Chatbot Connections Section */}
      <Card className="border border-border/60 shadow-sm bg-card">
        <CardHeader className="pb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-sm">
                <Bot className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-foreground">
                  Chatbot Connections
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Connect chatbots to make this action available to them
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="text-xs font-medium">
                {allChatbots.length} connected
              </Badge>
              <Button size="sm" className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-sm">
                <Plus className="h-4 w-4 mr-2" />
                Connect Chatbot
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {allChatbots.length > 0 ? <div className="space-y-3">
              {allChatbots.map(chatbot => <div key={chatbot.id} className="group flex items-center justify-between p-4 border border-border/50 rounded-xl bg-muted/30 hover:bg-muted/50 hover:border-border transition-all duration-200">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center shadow-sm">
                      <Bot className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">{chatbot.name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="default" className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs font-medium">
                          <div className="h-1.5 w-1.5 bg-emerald-500 rounded-full mr-1.5"></div>
                          Connected
                        </Badge>
                        <span className="text-xs text-muted-foreground">Chatbot Integration</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive hover:bg-destructive/10">
                      <Unlink className="h-4 w-4 mr-2" />
                      Disconnect
                    </Button>
                  </div>
                </div>)}
            </div> : <div className="text-center py-16">
              <div className="h-16 w-16 rounded-full bg-muted/50 mx-auto mb-4 flex items-center justify-center">
                <Users className="h-8 w-8 text-muted-foreground/60" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No chatbots connected</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto leading-relaxed">
                Connect chatbots to make this action available to them. Once connected, chatbots can use this action to perform tasks and provide enhanced assistance.
              </p>
              <Button className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-sm">
                <Plus className="h-4 w-4 mr-2" />
                Connect First Chatbot
              </Button>
            </div>}
        </CardContent>
      </Card>

      {/* Copilot Connections Section */}
      <Card className="border border-border/60 shadow-sm bg-card">
        <CardHeader className="pb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-sm">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-lg font-semibold text-foreground">
                  Copilot Connections
                </CardTitle>
                <p className="text-sm text-muted-foreground mt-1">
                  Connect copilots to enhance their capabilities with this action
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className="text-xs font-medium">
                {connectedCopilots.length} connected
              </Badge>
              <Button size="sm" className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 text-white shadow-sm">
                <Plus className="h-4 w-4 mr-2" />
                Connect Copilot
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {connectedCopilots.length > 0 ? <div className="space-y-3">
              {connectedCopilots.map(copilot => <div key={copilot.id} className="group flex items-center justify-between p-4 border border-border/50 rounded-xl bg-purple-50/30 hover:bg-purple-50/50 hover:border-purple-200/60 transition-all duration-200">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-purple-500 to-blue-600 flex items-center justify-center shadow-sm">
                      <Sparkles className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">{copilot.name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="default" className="bg-purple-100 text-purple-700 border-purple-200 text-xs font-medium">
                          <div className="h-1.5 w-1.5 bg-purple-500 rounded-full mr-1.5"></div>
                          Connected
                        </Badge>
                        <span className="text-xs text-muted-foreground">Copilot Integration</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-foreground hover:bg-background/80">
                      <Settings className="h-4 w-4 mr-2" />
                      Configure
                    </Button>
                    <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-destructive hover:bg-destructive/10">
                      <Unlink className="h-4 w-4 mr-2" />
                      Disconnect
                    </Button>
                  </div>
                </div>)}
            </div> : <div className="text-center py-16">
              <div className="h-16 w-16 rounded-full bg-purple-50/50 mx-auto mb-4 flex items-center justify-center">
                <Zap className="h-8 w-8 text-purple-400/60" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">No copilots connected</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto leading-relaxed">
                Connect copilots to make this action available to them. Copilots can use this action to enhance their capabilities and provide better assistance.
              </p>
              <Button className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-sm">
                <Plus className="h-4 w-4 mr-2" />
                Connect First Copilot
              </Button>
            </div>}
        </CardContent>
      </Card>
    </div>;
};